import { Alert, CircularProgress, Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { ReactElement } from 'react';

import { getMultiplexPanels, getMultiplexPanelsQueryKey } from 'api/multiplexPanels';
import { useCasesParams } from 'utils/useCasesParams';
import { CasesParams } from 'utils/useCasesParams/types';
import useStudy from 'utils/useStudy';
import { MultiplexSelectionAlert } from '../MultiplexSelectionAlert';
import { useMultiplexActionSelection } from '../useMultiplexActionSelection';
import { PanelSpotDetectionForm } from './PanelSpotDetectionForm';

export interface RunMultiplexPeakDetectionProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams?: CasesParams;
}

export const RunMultiplexPeakDetection = ({
  onClose,
  casesParams: casesParamsOverride,
}: RunMultiplexPeakDetectionProps): ReactElement => {
  const { casesParams: urlCasesParams } = useCasesParams();
  const selection = useMultiplexActionSelection(casesParamsOverride || urlCasesParams);
  const scope = selection.effectiveCasesParams || casesParamsOverride || urlCasesParams;
  const studyId = scope.studyId || (typeof scope.filters?.studyId === 'string' ? scope.filters.studyId : undefined);
  const studyQuery = useStudy(studyId);
  const panelsQuery = useQuery({
    queryKey: getMultiplexPanelsQueryKey(scope),
    queryFn: ({ signal }) => getMultiplexPanels(scope, signal),
    enabled: Boolean(selection.effectiveCasesParams && studyId),
  });
  if (selection.isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for this action."
      />
    );
  }
  if (selection.blockingMessage || !studyId) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={selection.blockingMessage || 'Select a study before spot detection.'}
      />
    );
  }
  if (panelsQuery.isError || studyQuery.isError) {
    return (
      <Alert severity="error">Panel settings could not be loaded. Close and reopen spot detection to retry.</Alert>
    );
  }
  if (panelsQuery.isLoading || studyQuery.isLoading || !panelsQuery.data) {
    return (
      <Alert severity="info" icon={<CircularProgress size={18} color="inherit" />}>
        Loading panel settings
      </Alert>
    );
  }
  const slideIds = selection.selectedMultiplexSlideIds;
  return (
    <Grid container direction="column" spacing={2}>
      {selection.infoMessage ? (
        <Grid item>
          <MultiplexSelectionAlert severity="info" title="This action runs on slides" message={selection.infoMessage} />
        </Grid>
      ) : null}
      <Grid item>
        <PanelSpotDetectionForm
          key={JSON.stringify([studyId, [...slideIds].sort()])}
          response={panelsQuery.data}
          casesParams={scope}
          selectedSlideIds={slideIds}
          initialUseRawDog={studyQuery.study?.settings?.mIF?.peakDetectionUseRawDog ?? true}
          onClose={onClose}
        />
      </Grid>
    </Grid>
  );
};
